import { useState } from 'react';
import { ChevronDownIcon, PlusIcon } from '@heroicons/react/24/outline';
import { cn, formatDateTime } from '../../../../../../../lib/utils.ts';
import {
    VatReturnApprovalStatusIcon,
    VatReturnApprovalStatusLabel,
    VatReturnDraftIcon,
    VatReturnDraftLabel,
} from './VatReturnApprovalStatus.tsx';
import type { VatReturnPeriod, VatReturnSelection } from '../types/index.ts';
import { vatReturnPeriodLabel, vatReturnPeriodMonths, vatReturnPeriodYear, vatReturnVersionCount } from '../utils/index.ts';

export interface VatReturnRailProps {
    periods: VatReturnPeriod[];
    clientName: string;
    selection: VatReturnSelection | null;
    isNewReturnOpen: boolean;
    isLoading: boolean;
    hasLoadError: boolean;
    onNewReturn: () => void;
    onSelectPeriod: (periodKey: string) => void;
    onSelectDraft: (periodKey: string) => void;
    onSelectVersion: (periodKey: string, snapshotId: string) => void;
}

function versionCount(period: VatReturnPeriod): string {
    return period.versions.length === 0 ? 'nog geen versies' : vatReturnVersionCount(period.versions.length);
}

function periodSubtitle(period: VatReturnPeriod): string {
    const months = vatReturnPeriodMonths(period.periodStart, period.periodEnd);
    return months ? `${months} · ${versionCount(period)}` : versionCount(period);
}

function groupByYear(periods: VatReturnPeriod[]): { year: number; periods: VatReturnPeriod[] }[] {
    const years: { year: number; periods: VatReturnPeriod[] }[] = [];
    for (const period of periods) {
        const year = vatReturnPeriodYear(period.periodStart);
        const group = years.find((candidate) => candidate.year === year);
        if (group) {
            group.periods.push(period);
        } else {
            years.push({ year, periods: [period] });
        }
    }
    return years;
}

function selectionSignature(selection: VatReturnSelection | null): string {
    if (!selection) {
        return '';
    }
    return selection.kind === 'draft' ? `${selection.periodKey}:draft` : `${selection.periodKey}:${selection.snapshotId}`;
}

function railItemClasses(isActive: boolean): string {
    return cn(
        'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm text-gray-900 hover:bg-gray-100',
        isActive && 'bg-superliteprimary text-primary hover:bg-superliteprimary',
    );
}

function loadStatusText(isLoading: boolean, hasLoadError: boolean, periodCount: number): string {
    if (isLoading) {
        return 'Aangiftes laden...';
    }
    if (hasLoadError) {
        return 'De opgeslagen aangiftes konden niet worden geladen.';
    }
    return periodCount === 0 ? 'Nog geen opgeslagen BTW-aangiftes.' : '';
}

export function VatReturnRail({
    periods,
    clientName,
    selection,
    isNewReturnOpen,
    isLoading,
    hasLoadError,
    onNewReturn,
    onSelectPeriod,
    onSelectDraft,
    onSelectVersion,
}: VatReturnRailProps) {
    const [toggledPeriodKeys, setToggledPeriodKeys] = useState<Record<string, boolean>>({});
    const selectedPeriodKey = selection?.periodKey ?? null;
    const [shownSelection, setShownSelection] = useState(selectionSignature(selection));
    if (shownSelection !== selectionSignature(selection)) {
        setShownSelection(selectionSignature(selection));
        if (selectedPeriodKey) {
            setToggledPeriodKeys((current) => ({ ...current, [selectedPeriodKey]: true }));
        }
    }

    function isExpanded(periodKey: string): boolean {
        return toggledPeriodKeys[periodKey] ?? periodKey === selectedPeriodKey;
    }

    function toggle(periodKey: string) {
        setToggledPeriodKeys((current) => ({ ...current, [periodKey]: !isExpanded(periodKey) }));
    }

    return (
        <aside aria-label="Opgeslagen aangiftes" className="flex w-full flex-col gap-4 border-gray-200 p-4 lg:w-80 lg:shrink-0 lg:border-r">
            <button
                type="button"
                onClick={onNewReturn}
                className={cn(
                    'flex items-center justify-center gap-2 rounded-md border px-3 py-2.5 text-sm font-medium',
                    isNewReturnOpen
                        ? 'border-primaryborder bg-superliteprimary text-primary'
                        : 'border-gray-300 bg-white text-gray-900 hover:bg-gray-50',
                )}
            >
                <PlusIcon className="h-4 w-4" aria-hidden="true" />
                Nieuwe aangifte
            </button>

            <p role="status" className={cn('px-2 text-sm', !isLoading && hasLoadError ? 'text-red-700' : 'text-gray-500')}>
                {loadStatusText(isLoading, hasLoadError, periods.length)}
            </p>

            {groupByYear(periods).map((group) => (
                <div key={group.year} className="flex flex-col gap-1">
                    <span className="px-2 pb-1 text-xs font-semibold tracking-wider text-gray-500">{group.year}</span>
                    {group.periods.map((period) => {
                        const label = vatReturnPeriodLabel(period.periodStart, period.periodEnd);
                        const expanded = isExpanded(period.key);
                        const isSelectedPeriod = !isNewReturnOpen && selectedPeriodKey === period.key;
                        const isDraftActive = isSelectedPeriod && selection?.kind === 'draft';
                        return (
                            <div key={period.key} className="flex flex-col gap-0.5">
                                <div className={cn('flex items-center rounded-md', isSelectedPeriod && 'bg-gray-100')}>
                                    <button
                                        type="button"
                                        onClick={() => onSelectPeriod(period.key)}
                                        aria-current={isSelectedPeriod}
                                        className="flex min-w-0 grow flex-col items-start gap-0.5 rounded-md py-2 pl-2.5 pr-1 text-left hover:bg-gray-100"
                                    >
                                        <span className="text-sm font-semibold text-gray-900">{label}</span>
                                        <span className="text-xs text-gray-500">{periodSubtitle(period)}</span>
                                        <span className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                                            {period.latestVersion && (
                                                <VatReturnApprovalStatusLabel approval={period.latestVersion} clientName={clientName} />
                                            )}
                                            {period.hasDraft && <VatReturnDraftLabel />}
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => toggle(period.key)}
                                        aria-expanded={expanded}
                                        aria-label={`${expanded ? 'Verberg' : 'Toon'} versies van ${label}`}
                                        className="rounded-md p-2 text-gray-600 hover:bg-gray-100"
                                    >
                                        <ChevronDownIcon
                                            className={cn('h-4 w-4 transition-transform', expanded && 'rotate-180')}
                                            aria-hidden="true"
                                        />
                                    </button>
                                </div>
                                {expanded && (
                                    <div className="ml-4 flex flex-col gap-px border-l border-gray-200 pl-2.5">
                                        {period.hasDraft && (
                                            <button
                                                type="button"
                                                onClick={() => onSelectDraft(period.key)}
                                                aria-current={isDraftActive}
                                                className={railItemClasses(isDraftActive)}
                                            >
                                                <span className="flex min-w-0 grow flex-col items-start">
                                                    <span>Concept</span>
                                                    <span className="text-xs text-gray-500">Niet opgeslagen</span>
                                                </span>
                                                <span className="sr-only">van {label}</span>
                                                <VatReturnDraftIcon />
                                            </button>
                                        )}
                                        {period.versions.map((version) => {
                                            const isActive = !isNewReturnOpen
                                                && selection?.kind === 'version'
                                                && selection.snapshotId === version.id;
                                            return (
                                                <button
                                                    key={version.id}
                                                    type="button"
                                                    onClick={() => onSelectVersion(period.key, version.id)}
                                                    aria-current={isActive}
                                                    className={railItemClasses(isActive)}
                                                >
                                                    <span className="flex min-w-0 grow flex-col items-start">
                                                        <span>Versie {version.version}</span>
                                                        <span className="text-xs text-gray-500">{formatDateTime(version.created_at)}</span>
                                                    </span>
                                                    <span className="sr-only">van {label}</span>
                                                    <VatReturnApprovalStatusIcon approval={version} clientName={clientName} />
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ))}
        </aside>
    );
}
